import { Logger } from '../../packages/logger/src/Logger';
import { ClientBuilderFactory } from '../remote/ClientBuilderFactory';
import { GraphURL } from '../util/URLUtils';
import {
  isGraphDirectoryForbidden,
  isGraphForbiddenError,
  isGraphForbiddenStatus,
  markGraphDirectoryForbidden,
} from './graphDirectoryAccess';
import * as MsTeamsUserService from './MsTeamsUserService';
import { GraphAPIError, IGraphUser, filterByPhoneNumber, filterGALHiddenUsers, isEmailQuery, isPhoneNumberQuery, maskPIIForLogging } from './MsTeamsUserService';

/**
 * Constants for paginated contact search
 */
const CONTACT_SEARCH_BATCH_SIZE = 100;  // Number of contacts to fetch per batch
const CONTACT_SEARCH_MAX_BATCHES = 100; // Maximum number of batches (total 10000 contacts) - Auto-retries until found

/**
 * Cross-checks personal contacts against directory users to filter out blocked users
 * If a personal contact exists as a directory user, apply GAL filtering
 * @param {IGraphUser[]} contacts - Personal contacts to validate
 * @returns {Promise<IGraphUser[]>} - Filtered contacts excluding blocked directory users
 */
async function filterBlockedDirectoryContacts(contacts: IGraphUser[]): Promise<IGraphUser[]> {
  if (!contacts?.length) return contacts;

  // Extract email addresses from personal contacts for directory lookup
  const contactEmails = contacts
    .map((contact) => contact.emailAddresses?.[0]?.address || contact.mail)
    .filter((email): email is string => Boolean(email?.trim()));

  if (contactEmails.length === 0) return contacts;

  try {
    // Check if any personal contacts exist as directory users
    const directoryCheckPromises = contactEmails.map(async (email) => {
      try {
        const url = `${GraphURL.Users}?$select=id,displayName,mail,accountEnabled,showInAddressList,userType&$count=true&$top=1&$filter=mail eq '${encodeURIComponent(email)}'`;
        const graphClient = await ClientBuilderFactory.getGraphClient();
        const response = await graphClient.get(url);
        
        if (response?.status === 200 && response.data?.value?.length > 0) {
          const directoryUser = response.data.value[0] as IGraphUser;
          // Apply GAL filtering to check if this directory user should be hidden
          const filteredUsers = filterGALHiddenUsers([directoryUser]);
          return { email, isBlocked: filteredUsers.length === 0 };
        }
        return { email, isBlocked: false };
      } catch (error) {
        return { email, isBlocked: false };
      }
    });

    const directoryCheckResults = await Promise.all(directoryCheckPromises);
    const blockedEmails = new Set(
      directoryCheckResults
        .filter(result => result.isBlocked)
        .map(result => result.email)
    );

    // Filter out personal contacts that correspond to blocked directory users
    return contacts.filter(contact => {
      const contactEmail = contact.emailAddresses?.[0]?.address || contact.mail;
      if (contactEmail && blockedEmails.has(contactEmail)) {
        return false;
      }
      return true;
    });

  } catch (error) {
    Logger.error(`Error in filterBlockedDirectoryContacts: ${error}`);
    // On error, return original contacts to avoid breaking functionality
    return contacts;
  }
}

/**
 *
 */
export async function listAllContacts(): Promise<
  MsTeamsUserService.IGraphUser[]
> {
  try {
    const graphClient = await ClientBuilderFactory.getGraphClient();
    const response = await graphClient.get(
      `${GraphURL.Contacts}?$select=id,givenName,surname,displayName,mobilePhone,emailAddresses,businessPhones,homePhones&$count=true&$top=100`
    );
    if (response?.status === 200){
      const data = response?.data?.value as MsTeamsUserService.IGraphUser[];
      //Process the data to derive the displayname
      const processedData = data?.map((contact) => {
        if (contact?.displayName) {
          return contact;
        } else {
          const displayName = `${contact?.givenName} ${contact?.surname}`;
          return { ...contact, displayName };
        }
      })
      
      // Apply filtering to prevent blocked directory users from appearing in personal contacts
      return await filterBlockedDirectoryContacts(processedData);
    } else {
      const error = response as GraphAPIError;
      if (error.response?.status === 404) {
          return [] as IGraphUser[];
      }     
    }
    return Promise.reject(response);
  } catch (error) {
    return [];
  }
}

/**
 * Helper function to process contact display names
 * @param {IGraphUser[]} contacts - Array of contacts
 * @returns {IGraphUser[]} - Contacts with processed display names
 */
function processContactDisplayNames(contacts: IGraphUser[]): IGraphUser[] {
  return contacts.map((contact) => {
    if (contact?.displayName) {
      return contact;
    }
    const displayName = `${contact?.givenName || ''} ${contact?.surname || ''}`.trim();
    return { ...contact, displayName };
  });
}

/**
 * Helper function to filter contacts by email (checks both mail and emailAddresses)
 * @param {IGraphUser[]} contacts - Array of contacts
 * @param {string} emailQuery - Email query string
 * @returns {IGraphUser[]} - Filtered contacts
 */
function filterContactsByEmail(contacts: IGraphUser[], emailQuery: string): IGraphUser[] {
  const normalizedQuery = emailQuery.toLowerCase().trim();
  
  // Detect domain-only patterns
  const isDomainOnlyWithAt = normalizedQuery.startsWith('@'); // e.g., "@cisco.com"
  const isDomainOnlyPlain = !normalizedQuery.includes('@'); // e.g., "cisco.com"
  const isDomainOnly = isDomainOnlyWithAt || isDomainOnlyPlain;
  
  return contacts.filter(contact => {
    // Check mail field
    if (contact.mail) {
      const normalized = contact.mail.toLowerCase();
      
      // Domain-only search
      if (isDomainOnly) {
        let domainToMatch: string;
        
        if (isDomainOnlyWithAt) {
          // "@cisco.com" → match emails ending with "@cisco.com"
          domainToMatch = normalizedQuery; // Keep the @
        } else {
          // "cisco.com" → match emails ending with "@cisco.com"
          domainToMatch = `@${normalizedQuery}`;
        }
        
        if (normalized.endsWith(domainToMatch)) {
          return true;
        }
      } else {
        // Regular email search (partial or full)
        if (normalized.includes(normalizedQuery)) {
          return true;
        }
      }
    }
    
    // Check emailAddresses array
    if (contact.emailAddresses && Array.isArray(contact.emailAddresses)) {
      return contact.emailAddresses.some(emailAddr => {
        if (!emailAddr.address) return false;
        const normalized = emailAddr.address.toLowerCase();
        
        // Domain-only search
        if (isDomainOnly) {
          let domainToMatch: string;
          
          if (isDomainOnlyWithAt) {
            domainToMatch = normalizedQuery; // Keep the @
          } else {
            domainToMatch = `@${normalizedQuery}`;
          }
          
          return normalized.endsWith(domainToMatch);
        }
        
        // Regular email search
        return normalized.includes(normalizedQuery);
      });
    }
    
    return false;
  });
}

/**
 * Paginated search helper for contacts email/phone searches
 * Fetches contacts in batches until matches are found or max batches reached
 * 
 * @param {string} query - The search query
 * @param {(contacts: IGraphUser[], query: string) => IGraphUser[]} filterFn - Function to filter contacts
 * @param {string} searchType - Type of search for logging ('Email' or 'Phone')
 * @param {number} resultLimit - Maximum number of results to return
 * @returns {Promise<IGraphUser[]>} - Matched contacts
 */
async function paginatedContactSearch(
  query: string,
  filterFn: (contacts: IGraphUser[], query: string) => IGraphUser[],
  searchType: string,
  resultLimit: number
): Promise<IGraphUser[]> {
  const graphClient = await ClientBuilderFactory.getGraphClient();
  
  let allMatches: IGraphUser[] = [];
  let skipToken: string | null = null;
  let batchCount = 0;
  
  while (batchCount < CONTACT_SEARCH_MAX_BATCHES) {
    batchCount++;
    
    // Build URL with pagination
    let url = `${GraphURL.Contacts}?$select=id,displayName,givenName,surname,mobilePhone,emailAddresses,businessPhones,homePhones&$count=true&$top=${CONTACT_SEARCH_BATCH_SIZE}`;
    
    if (skipToken) {
      url += `&$skiptoken=${skipToken}`;
    }
    
    try {
      const response = await graphClient.get(url);
      
      if (response?.status !== 200) {
        const error = response as GraphAPIError;
        if (error.response?.status === 404) {
          break;
        }
        return Promise.reject(response);
      }
      
      const contacts = response.data.value as IGraphUser[];
      
      if (contacts.length === 0) break;
      
      // Process display names
      const processedContacts = processContactDisplayNames(contacts);
      
      // Apply email/phone filtering
      const matches = filterFn(processedContacts, query);
      
      Logger.info(`[${searchType} Search - Contacts] Batch ${batchCount}: Fetched ${contacts.length} | Matches: ${matches.length} | Total matches: ${allMatches.length + matches.length}`);
      
      if (matches.length > 0) {
        allMatches.push(...matches);
        
        // If we have enough matches, stop fetching
        if (allMatches.length >= resultLimit) {
          Logger.info(`[${searchType} Search - Contacts] Stopping early - found ${allMatches.length} matches (limit: ${resultLimit})`);
          break;
        }
      }
      
      // Check if there are more pages
      skipToken = response.data['@odata.nextLink'] 
        ? new URL(response.data['@odata.nextLink']).searchParams.get('$skiptoken')
        : null;
      
      if (!skipToken) break;
      
    } catch (error) {
      Logger.error(`[${searchType} Search - Contacts] Error in batch ${batchCount}: ${error}`);
      break;
    }
  }
  
  // Apply blocked directory contact filtering
  const blockedFiltered = await filterBlockedDirectoryContacts(allMatches);
  
  Logger.info(`[${searchType} Search - Contacts] Completed: ${batchCount} batches, returning ${Math.min(blockedFiltered.length, resultLimit)} of ${blockedFiltered.length} matches (${allMatches.length} before directory filter)`);
  
  // Return limited results
  return blockedFiltered.slice(0, resultLimit);
}

/**
 * Reverse phone lookup across Outlook personal contacts (paginated Graph scan).
 * Used by call-history enrichment — dialpad search skips Graph for phone queries.
 */
export async function lookupOutlookContactByPhone(
  phone: string,
  limit = 10
): Promise<IGraphUser[]> {
  const trimmed = phone?.trim();
  if (!trimmed || isGraphDirectoryForbidden()) {
    return [];
  }

  try {
    return await paginatedContactSearch(trimmed, filterByPhoneNumber, 'Phone', limit);
  } catch (error) {
    if (isGraphForbiddenError(error)) {
      markGraphDirectoryForbidden('outlook phone lookup');
      return [];
    }
    Logger.error(`Error in lookupOutlookContactByPhone: ${error}`);
    return [];
  }
}

/**
 * Fetches contacts by name, phone number, or email address
 * @param query name prefix, phone number, or email address to search for
 * @param limit maximum number of results
 * @returns {Promise<IGraphUser[]>} contacts who match the query string
 */
export async function fetchContacts(
  query: string,
  limit = 100
): Promise<IGraphUser[]> {
  try {
    // Input validation
    if (!query?.trim()) {
      return [];
    }

    const trimmedQuery = query.trim();
    const isEmailSearch = isEmailQuery(trimmedQuery);
    const isPhoneSearch = !isEmailSearch && isPhoneNumberQuery(trimmedQuery);

    Logger.info(`[Contact Search] Query: "${maskPIIForLogging(trimmedQuery)}" | Type: ${isEmailSearch ? 'EMAIL' : isPhoneSearch ? 'PHONE' : 'NAME'} | Limit: ${limit}`);

    if (isEmailSearch) {
      return paginatedContactSearch(trimmedQuery, filterContactsByEmail, 'Email', limit);
    } else if (isPhoneSearch) {
      // Phone/extension search is directory-only (DSS). Do not call Graph contacts API.
      Logger.info(`[Contact Search] Skipping Graph contacts for phone/extension query - directory uses DSS only`);
      return [];
    } else {
      if (isGraphDirectoryForbidden()) {
        Logger.info('[Contact Search] Skipping Graph contacts — directory API forbidden (403)');
        return [];
      }
      // Name search: Use server-side filtering for efficiency
      const escapedQuery = encodeURIComponent(trimmedQuery.replace(/'/g, "''"));
      const url = `${GraphURL.Contacts}?$select=id,displayName,givenName,surname,mobilePhone,emailAddresses,businessPhones,homePhones&$count=true&$top=${limit}&$filter=startswith(givenName,'${escapedQuery}') or startswith(surname,'${escapedQuery}') or startswith(displayName,'${escapedQuery}')`;
      const graphClient = await ClientBuilderFactory.getGraphClient();
      const response = await graphClient.get(url);
      
      if (isGraphForbiddenStatus(response?.status)) {
        markGraphDirectoryForbidden('outlook contacts search');
        return [];
      }
      if (response?.status === 200){
        const data = response?.data?.value as IGraphUser[];
        const processedData = processContactDisplayNames(data);
        const filtered = await filterBlockedDirectoryContacts(processedData);
        return filtered;
      } else {
        const error = response as GraphAPIError;
        if (error.response?.status === 404) {
            return [] as IGraphUser[];
        }
        if (isGraphForbiddenStatus(error.response?.status)) {
          markGraphDirectoryForbidden('outlook contacts search');
          return [];
        }
      }
      return Promise.reject(response);
    }
  } catch (error) {
    if (isGraphForbiddenError(error)) {
      markGraphDirectoryForbidden('outlook contacts search');
      return [];
    }
    Logger.error(`Error in fetchContacts: ${error}`);
    return [];
  }
}

/**
 * Enhanced search for contacts that handles multi-term queries
 * Searches for contacts where ANY term matches givenName, surname, or displayName
 * Then filters to include only contacts that contain ALL search terms
 *
 * @param {string} query search query (can be multi-word like "John Smith", phone number, or email address)
 * @param {number} limit maximum number of results per term search
 * @returns {Promise<IGraphUser[]>} contacts who match all search terms
 */
export async function fetchContactsEnhanced(
  query: string,
  limit = 100
): Promise<IGraphUser[]> {
  try {
    if (!query?.trim()) {
      return [];
    }

    // For email addresses, don't split - search as a single term
    if (isEmailQuery(query)) {
      return fetchContacts(query, limit);
    }

    // For phone numbers, don't split - search as a single term
    if (isPhoneNumberQuery(query)) {
      return fetchContacts(query, limit);
    }

    const searchTerms = query.trim().split(/\s+/);
    
    // If single term, use regular search for performance
    if (searchTerms.length === 1) {
      return fetchContacts(query, limit);
    }

    // Multi-term search: collect all results from individual term searches
    const allResults = new Map<string, IGraphUser>();
    const termMatchCounts = new Map<string, number>();

    for (const term of searchTerms) {
      const termResults = await fetchContacts(term, limit);
      termResults.forEach(contact => {
        allResults.set(contact.id, contact);
        termMatchCounts.set(contact.id, (termMatchCounts.get(contact.id) || 0) + 1);
      });
    }

    // Filter to include only contacts that match ALL search terms
    const filteredResults = Array.from(allResults.values()).filter(contact => {
      const fullText = `${contact.givenName || ''} ${contact.surname || ''} ${contact.displayName || ''}`.toLowerCase();
      return searchTerms.every(term => fullText.includes(term.toLowerCase()));
    });

    // Sort by relevance: contacts matching more terms first, then alphabetically
    filteredResults.sort((a, b) => {
      const aMatches = termMatchCounts.get(a.id) || 0;
      const bMatches = termMatchCounts.get(b.id) || 0;
      
      if (aMatches !== bMatches) {
        return bMatches - aMatches; // More matches first
      }
      
      // Same number of matches, sort alphabetically
      const aName = a.displayName || `${a.givenName || ''} ${a.surname || ''}`;
      const bName = b.displayName || `${b.givenName || ''} ${b.surname || ''}`;
      return aName.localeCompare(bName);
    });

    // Limit final results and filter out blocked directory users
    const limitedResults = filteredResults.slice(0, limit);
    return filterBlockedDirectoryContacts(limitedResults);

  } catch (error) {
    console.error('Error in fetchContactsEnhanced:', error);
    // Fallback to regular search
    return fetchContacts(query, limit);
  }
}

/**
 *
 * @param contactId
 */
export async function getContact(contactId: string): Promise<IGraphUser | void> {
    // Input validation
    if (!contactId?.trim()) {
      return undefined;
    }
    
    const url = `${GraphURL.Contacts}/${contactId}?$select=id,displayName,mobilePhone,emailAddresses,businessPhones,homePhones`;
    const graphClient = await ClientBuilderFactory.getGraphClient();
    const response = await graphClient.get(url);
    if (response.status === 200) {
        return response.data as IGraphUser;
    } else {
      const error = response as GraphAPIError;
      // Handle expected errors gracefully - return undefined instead of rejecting
      if (error.response?.status === 404 || 
          error.response?.data?.error?.code === 'ErrorInvalidIdMalformed') {
        return;
      }
    }
    return Promise.reject(response);
}

/**
 * Fetches a contact's avatar using their contact ID
 *
 * @param {string} contactId contact's image to fetch
 * @returns {Promise<string>} image url
 */
export async function fetchContactAvatar(
  contactId: string
): Promise<string | undefined> {
  const url = `${GraphURL.Contacts}/${contactId}/photo/$value`;
  const graphClient = await ClientBuilderFactory.getGraphClient();
  const response = await graphClient.get(url, {
    responseType: 'blob',
  });
  if (response.status === 200) return URL.createObjectURL(response.data);
  return undefined;
}